import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, CircularProgress, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { AthletesFilter, StatusAtleta } from '../../types/atleta';

export type AvailableFilterKey = 'nomeUsuario' | 'cpf' | 'cidade' | 'status';

export interface AthletesTableToolbarProps {
  availableFilters?: AvailableFilterKey[];
  initialFilters?: AthletesFilter;
  value?: AthletesFilter;
  onChange?: (filters: AthletesFilter) => void;
  onApply?: (filters: AthletesFilter) => void;
  onReset?: () => void;
  disabled?: boolean;

  placeholders?: Partial<{
    nomeUsuario: string;
    cpf: string;
    cidade: string;
    status: string;
  }>;

  statusOptions?: { label: string; value: StatusAtleta }[];

  fetchCidades?: (q: string) => Promise<Array<{ label: string; value: string }>>;

  showRefreshButton?: boolean;
  onRefresh?: () => void;
}

const DEFAULT_AVAILABLE: AvailableFilterKey[] = ['nomeUsuario','cpf','cidade','status'];
const DEFAULT_STATUS_OPTIONS: { label: string; value: StatusAtleta }[] = [
  { label: 'Ativo', value: 'ATIVO' },
  { label: 'Inativo', value: 'INATIVO' },
];


const useDebounced = (fn: (...args: any[]) => void, delay: number) => {
  const ref = useRef<number | undefined>(undefined);
  const cb = useCallback((...args: any[]) => {
    if (ref.current) window.clearTimeout(ref.current);
    ref.current = window.setTimeout(() => fn(...args), delay);
  }, [fn, delay]);
  useEffect(() => () => { if (ref.current) window.clearTimeout(ref.current); }, []);
  return cb;
};

const onlyDigits = (v: string) => v.replace(/\D+/g, '');

const AthletesTableToolbar: React.FC<AthletesTableToolbarProps> = ({
  availableFilters = DEFAULT_AVAILABLE,
  initialFilters,
  value,
  onChange,
  onApply,
  onReset,
  disabled,
  placeholders,
  statusOptions = DEFAULT_STATUS_OPTIONS,
  fetchCidades,
  showRefreshButton,
  onRefresh,
}) => {
  const isControlled = !!value;
  const [filters, setFilters] = useState<AthletesFilter>(initialFilters || {});
  useEffect(() => { if (isControlled && value) setFilters(value); }, [isControlled, value]);

  const [nomeInput, setNomeInput] = useState('');
  const [cpfInput, setCpfInput] = useState('');
  const [cidadeInput, setCidadeInput] = useState('');
  const [cidadeOptions, setCidadeOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [loadingCidade, setLoadingCidade] = useState(false);
  const [cidadeSelected, setCidadeSelected] = useState<{ label: string; value: string } | null>(null);

  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const toolbarGap = theme.spacing(2);
  const toolbarContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: isLargeScreen ? 'nowrap' : 'wrap',
    alignItems: 'center',
    gap: toolbarGap,
  };

  useEffect(() => {
    // Sync local inputs from controlled value
    if (isControlled && value) {
      setNomeInput(value.nomeUsuario || '');
      setCpfInput(value.cpf || '');
      setCidadeInput('');
    }
  }, [isControlled, value?.nomeUsuario, value?.cpf, value?.cidade]);

  const emitChange = useCallback((next: AthletesFilter) => {
    if (onChange) onChange(next);
    if (!isControlled) setFilters(next);
  }, [isControlled, onChange]);



  const debouncedNome = useDebounced((v: string) => {
    const next = { ...filters, nomeUsuario: v ? v : null };
    emitChange(next);
  }, 350);

  const debouncedCidadeFetch = useDebounced(async (q: string) => {
    if (!fetchCidades) return;
    setLoadingCidade(true);
    try {
      const list = await fetchCidades(q || '');
      setCidadeOptions(list);
    } finally {
      setLoadingCidade(false);
    }
  }, 350);

  useEffect(() => { if (availableFilters.includes('nomeUsuario')) debouncedNome(nomeInput); }, [nomeInput]);
  useEffect(() => { if (availableFilters.includes('cidade') && fetchCidades) debouncedCidadeFetch(cidadeInput); }, [cidadeInput, fetchCidades]);
  // Sincroniza selecao quando o valor controlado muda externamente
  useEffect(() => {
    if (!isControlled) return;
    const id = value?.cidade || null;
    if (!id) {
      setCidadeSelected(null);
      return;
    }
    if (cidadeSelected?.value === id) return;
    const found = cidadeOptions.find(o => o.value === id);
    setCidadeSelected(found ?? cidadeSelected ?? { label: id, value: id });
  }, [isControlled, value?.cidade, cidadeOptions]);
  // Quando opcoes mudam, se houver um id selecionado no filtro, materializa label atualizado
  useEffect(() => {
    if (filters.cidade) {
      const found = cidadeOptions.find(o => o.value === filters.cidade);
      if (found) setCidadeSelected(found);
    }
  }, [cidadeOptions]);

  const cpfDigits = useMemo(() => onlyDigits(cpfInput || ''), [cpfInput]);
  const cpfInvalid = useMemo(() => {
    if (!cpfDigits) return false; // empty is ok
    return cpfDigits.length !== 11;
  }, [cpfDigits]);

  const handleCpfChange = (v: string) => {
    const digits = onlyDigits(v || '');
    setCpfInput(digits);
    emitChange({ ...filters, cpf: digits ? digits : null });
  };

  const handleCidadeSelect = (_: any, v: { label: string; value: string } | null) => {
    if (!v) {
      setCidadeSelected(null);
      emitChange({ ...filters, cidade: null });
      setCidadeInput('');
      return;
    }
    setCidadeSelected(v);
    emitChange({ ...filters, cidade: v.value || null });
    setCidadeInput('');
  };

  // If there is a selected city id, try to materialize options to resolve the label
  useEffect(() => {
    if (availableFilters.includes('cidade') && fetchCidades && filters.cidade && cidadeOptions.length === 0) {
      fetchCidades('')
        .then((list) => setCidadeOptions(list))
        .catch(() => void 0);
    }
  }, [availableFilters, fetchCidades, filters.cidade, cidadeOptions.length]);

  const handleReset = () => {
    const next = initialFilters || {};
    setNomeInput(next.nomeUsuario || '');
    setCpfInput(next.cpf || '');
    setCidadeInput('');
    setCidadeSelected(null);
    emitChange(next);
    if (onReset) onReset();
  };

  const p = {
    nomeUsuario: placeholders?.nomeUsuario ?? 'Nome ou usuário',
    cpf: placeholders?.cpf ?? 'CPF (apenas dígitos)',
    cidade: placeholders?.cidade ?? 'Cidade',
    status: placeholders?.status ?? 'Status',
  };

  return (
    <div className="card w-100 border-0 bg-white shadow-xs p-3 mb-3">
      <div style={toolbarContainerStyle}>
        {availableFilters.includes('nomeUsuario') && (
          <div >
            <TextField
              label={p.nomeUsuario}
              size="small"
              fullWidth
              disabled={disabled}
              value={nomeInput}
              onChange={(e) => setNomeInput(e.target.value)}
            />
          </div>
        )}

        {availableFilters.includes('cpf') && (
          <div >
            <TextField
              label={p.cpf}
              size="small"
              fullWidth
              disabled={disabled}
              value={cpfInput}
              onChange={(e) => handleCpfChange(e.target.value)}
              error={cpfInvalid}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            />
          </div>
        )}

        {availableFilters.includes('cidade') && (
          <div style={{ flex: '1 1 240px', minWidth: 220 }}>
            {fetchCidades ? (
              <Autocomplete
                options={cidadeOptions}
                getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
                isOptionEqualToValue={(opt, val) => opt.value === val.value}
                loading={loadingCidade}
                disabled={disabled}
                inputValue={cidadeInput}
                onInputChange={(_, v) => setCidadeInput(v)}
                value={cidadeSelected}
                onChange={handleCidadeSelect}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={p.cidade}
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCidade ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            ) : (
              <TextField
                label={p.cidade}
                size="small"
                fullWidth
                disabled={disabled}
                value={cidadeInput}
                onChange={(e) => {
                  setCidadeInput(e.target.value);
                  emitChange({ ...filters, cidade: e.target.value || null });
                }}
              />
            )}
          </div>
        )}

        {availableFilters.includes('status') && (
          <div >
            <Select
              size="small"
              fullWidth
              value={(filters.status as any) || ''}
              onChange={(e: SelectChangeEvent) => emitChange({ ...filters, status: (e.target.value || null) as StatusAtleta | null })}
              displayEmpty
              disabled={disabled}
            >
              <MenuItem value="">
                <em>{p.status}</em>
              </MenuItem>
              {statusOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </div>
        )}

        {/* <div style={{ flex: '0 1 auto' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={disabled || cpfInvalid}
              onClick={() => onApply && onApply(filters)}
            >
              Aplicar
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              disabled={disabled}
              onClick={handleReset}
            >
              Limpar
            </Button>
            {showRefreshButton && onRefresh && (
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                disabled={disabled}
                onClick={onRefresh}
              >
                Atualizar
              </Button>
            )}
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default AthletesTableToolbar;
//
